import React, { useMemo, useState } from 'react';
import { formatTime, toDate, phoneHref, waHref } from '../services/firebase';
import {
  ACTIVE_STATUSES, STATUS_CLASS, STATUS_LABEL, RANGE_OPTIONS, istDayKey, minutesAgo, rangeBounds, inBounds,
} from '../utils/rides';
import { hasDocPendingReview } from '../utils/driverDocs';
import { useLang } from '../lang/LangContext';

export default function Dashboard({ rides = [], drivers = [], users = [], openSos = 0, stuckCount = 0, onNavigate }) {
  const { lang, t } = useLang();
  const [range, setRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const bounds = useMemo(() => rangeBounds(range, customFrom, customTo), [range, customFrom, customTo]);
  const rangeLabel = (RANGE_OPTIONS.find(([v]) => v === range) || [])[1] || '';

  const stats = useMemo(() => {
    const active = rides.filter((r) => ACTIVE_STATUSES.includes(r.status));
    const searching = rides.filter((r) => r.status === 'searching');
    const stuck = searching.filter((r) => (minutesAgo(toDate(r.createdAt)) || 0) >= 5);
    const todayCompleted = rides.filter((r) => r.status === 'completed' && inBounds(toDate(r.completedAt || r.updatedAt), bounds));
    const todayCancelled = rides.filter((r) => r.status === 'cancelled' && inBounds(toDate(r.cancelledAt || r.updatedAt), bounds));
    // Rides still searching belong to "now", so they only count when the range reaches today.
    const rangeIncludesToday = !bounds || bounds[1] >= istDayKey(Date.now());
    const todayTotalRequests = todayCompleted.length + todayCancelled.length + (rangeIncludesToday ? searching.length : 0);
    const todayFare = todayCompleted.reduce((s, r) => s + (Number(r.fare) || 0), 0);
    const avgFare = todayCompleted.length ? Math.round(todayFare / todayCompleted.length) : 0;
    const completionRate = todayTotalRequests > 0 ? `${Math.round((todayCompleted.length / todayTotalRequests) * 100)}%` : '—';

    const online = drivers.filter((d) => d.isOnline && d.isApproved && !d.isBlocked);
    const onTrip = drivers.filter((d) => d.isOnline && rides.some((r) => r.driverId === d.id && ['driver_assigned', 'arrived', 'started'].includes(r.status)));
    const pending = drivers.filter((d) => !d.isApproved && !d.isBlocked);
    const docsWaiting = drivers.filter(hasDocPendingReview);
    const totalPendingDues = drivers.reduce((s, d) => s + (Number(d.pendingDues) || 0), 0);
    const blockedUsers = users.filter((u) => u.isBlocked).length;
    const flaggedPrankUsers = users.filter((u) => u.isFlaggedForPrank).length;

    return {
      active: active.length,
      searching: searching.length,
      stuck: stuckCount || stuck.length,
      online: online.length,
      onTrip: onTrip.length,
      pending: pending.length,
      docsWaiting: docsWaiting.length,
      passengers: users.length,
      captains: drivers.length,
      todayRides: todayCompleted.length,
      todayCancelled: todayCancelled.length,
      todayFare,
      avgFare,
      completionRate,
      totalPendingDues,
      blockedUsers,
      flaggedPrankUsers,
      openSos,
    };
  }, [rides, drivers, users, openSos, stuckCount, bounds]);

  const live = rides.filter((r) => ACTIVE_STATUSES.includes(r.status)).slice(0, 10);
  const onlineDrivers = drivers.filter((d) => d.isOnline && d.isApproved && !d.isBlocked).slice(0, 6);

  return (
    <>
      <div className="toolbar">
        <span className="muted">{t('Fares & completion for')}</span>
        <select value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range">
          {RANGE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
        </select>
        {range === 'custom' && (
          <>
            <input type="date" style={{ minWidth: 0 }} value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From date" />
            <span className="muted">→</span>
            <input type="date" style={{ minWidth: 0 }} value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} aria-label="To date" />
          </>
        )}
      </div>

      {/* Module E: Executive Business Dashboard (5 Cards) */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 16 }}>
        <div className="stat-card green">
          <div className="stat-label">
            {range === 'today' ? t("TODAY'S GMV (TOTAL FARES)") : `${t('GMV (TOTAL FARES)')} · ${t(rangeLabel).toUpperCase()}`}
          </div>
          <div className="stat-value green">₹{stats.todayFare.toLocaleString('en-IN')}</div>
          <div className="stat-sub">{stats.todayRides} {t('trips completed')} · ₹{stats.avgFare} {t('avg fare')}</div>
        </div>

        <div className="stat-card blue">
          <div className="stat-label">
            {range === 'today' ? t('COMPLETION RATE') : `${t('COMPLETION RATE')} · ${t(rangeLabel).toUpperCase()}`}
          </div>
          <div className="stat-value blue">{stats.completionRate}</div>
          <div className="stat-sub">{stats.todayCancelled} {t('cancelled')} · {stats.searching} {t('searching')}</div>
        </div>

        <div className="stat-card orange">
          <div className="stat-label">{t('LIVE ACTIVE TRIPS')}</div>
          <div className="stat-value orange">{stats.active}</div>
          <div className="stat-sub">
            <span style={{ color: stats.searching > 0 ? '#fb923c' : 'inherit' }}>{stats.searching} {t('matching')}</span>
            {' · '}
            <span style={{ color: stats.stuck > 0 ? '#ef4444' : 'inherit' }}>{stats.stuck} {t('stuck')}</span>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-label">{t('ONLINE FLEET (PILER)')}</div>
          <div className="stat-value green">{stats.online}</div>
          <div className="stat-sub">
            <span>{stats.onTrip} {t('on active trips')}</span>
            {' · '}
            <span style={{ color: stats.pending > 0 ? '#eab308' : 'inherit' }}>{stats.pending} {t('pending KYC')}</span>
          </div>
        </div>

        <div className="stat-card red">
          <div className="stat-label">{t('COMMISSION DUES OWED')}</div>
          <div className="stat-value red">₹{stats.totalPendingDues.toLocaleString('en-IN')}</div>
          <div className="stat-sub">
            {stats.flaggedPrankUsers > 0 ? (
              <span style={{ color: '#f87171' }}>🚫 {stats.flaggedPrankUsers} {t('prank flagged users')}</span>
            ) : (
              t('All safety parameters clean')
            )}
          </div>
        </div>
      </div>

      {/* 1-Click Operations Command Hub */}
      <div className="ops-hub-grid">
        <div className="ops-hub-card" onClick={() => onNavigate?.('dispatch')}>
          <div className="ops-hub-icon dispatch">📞</div>
          <div>
            <div className="ops-hub-title">{t('Phone Dispatch')}</div>
            <div className="ops-hub-desc">{t('Take manual passenger calls')}</div>
          </div>
        </div>

        <div className="ops-hub-card" onClick={() => onNavigate?.('broadcast')}>
          <div className="ops-hub-icon broadcast">📢</div>
          <div>
            <div className="ops-hub-title">{t('Fleet Broadcast')}</div>
            <div className="ops-hub-desc">{t('1-Click rain & rush alerts')}</div>
          </div>
        </div>

        <div className="ops-hub-card" onClick={() => onNavigate?.('sos')}>
          <div className="ops-hub-icon sos">🚨</div>
          <div>
            <div className="ops-hub-title">{t('SOS Alarm Desk')}</div>
            <div className="ops-hub-desc">
              {stats.openSos > 0
                ? `${stats.openSos} ${lang === 'te' ? 'యాక్టివ్ అత్యవసర పరిస్థితులు' : 'active emergencies'}`
                : t('Zero active distress')}
            </div>
          </div>
        </div>

        <div className="ops-hub-card" onClick={() => onNavigate?.('map')}>
          <div className="ops-hub-icon radar">🗺️</div>
          <div>
            <div className="ops-hub-title">{t('Live Fleet Radar')}</div>
            <div className="ops-hub-desc">{t('Real-time GPS tracking in Piler')}</div>
          </div>
        </div>
      </div>

      {/* Urgent Operations Banner */}
      {(stats.openSos > 0 || stats.stuck > 0 || stats.pending > 0 || stats.docsWaiting > 0 || stats.online === 0) && (
        <div className="card alert-card" style={{ borderLeft: '4px solid #ef4444' }}>
          <div className="card-title" style={{ color: '#f87171', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⚠️ {t('Operations Attention Required')}</span>
          </div>
          <ul className="alert-list" style={{ display: 'grid', gap: '8px' }}>
            {stats.openSos > 0 && (
              <li style={{ color: '#f87171' }}>
                <strong>
                  {lang === 'te'
                    ? `🚨 ${stats.openSos} యాక్టివ్ SOS అలర్ట్(లు) జరుగుతున్నాయి!`
                    : `🚨 ${stats.openSos} active SOS distress alert(s) in progress!`}
                </strong>
                {' — '}
                <button type="button" className="linkish" onClick={() => onNavigate?.('sos')}>{t('Open SOS Desk Now →')}</button>
              </li>
            )}
            {stats.stuck > 0 && (
              <li>
                <strong>
                  {lang === 'te'
                    ? `⏱️ ${stats.stuck} నిలిచిపోయిన రైడ్ అభ్యర్థన(లు)`
                    : `⏱️ ${stats.stuck} stuck ride request(s)`}
                </strong>
                {lang === 'te' ? ' 5 నిమిషాలకు పైగా కెప్టెన్ లేకుండా వేచి ఉన్నాయి.' : ' waiting > 5 minutes without captain.'}
                {' — '}
                <button type="button" className="linkish" onClick={() => onNavigate?.('stuck')}>{t('Open Stuck Queue →')}</button>
              </li>
            )}
            {stats.docsWaiting > 0 && (
              <li>
                <strong>
                  {lang === 'te'
                    ? `🪪 ${stats.docsWaiting} అన్న(లు) డాక్యుమెంట్(లు) రివ్యూ కోసం వేచి ఉన్నారు`
                    : `🪪 ${stats.docsWaiting} Anna${stats.docsWaiting > 1 ? 's have' : ' has'} document(s) waiting for review`}
                </strong>
                {' — '}
                <button type="button" className="linkish" onClick={() => onNavigate?.('drivers', { filter: 'docs_review' })}>{t('Open review queue →')}</button>
              </li>
            )}
            {stats.pending > 0 && (
              <li>
                <strong>
                  {lang === 'te'
                    ? `🪪 ${stats.pending} కెప్టెన్ దరఖాస్తుదారు(లు)`
                    : `🪪 ${stats.pending} captain applicant(s)`}
                </strong>
                {lang === 'te' ? ' మొదటి ఆమోదం కోసం వేచి ఉన్నారు.' : ' awaiting first approval.'}
                {' — '}
                <button type="button" className="linkish" onClick={() => onNavigate?.('drivers', { filter: 'pending' })}>{t('See applicants →')}</button>
              </li>
            )}
            {stats.online === 0 && (
              <li style={{ color: '#f97316' }}>
                <strong>
                  {lang === 'te'
                    ? '⚠️ పిలేరులో ప్రస్తుతం కెప్టెన్లు ఆన్లైన్లో లేరు.'
                    : '⚠️ No captains currently online in Piler.'}
                </strong>
                {' '}
                {lang === 'te' ? 'ప్రయాణికులు డిస్పాచ్ ఆలస్యాన్ని అనుభవిస్తారు.' : 'Passengers will experience dispatch delays.'}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Main Operations Split: Live Rides Stream & Online Fleet */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 0.8fr)', gap: '20px' }}>
        {/* Live Activity Table */}
        <div className="card">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t('🛺 Live Ride Operations Stream')}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onNavigate?.('rides')}
            >
              {t('View All')} ({rides.length}) →
            </button>
          </div>

          {live.length === 0 ? (
            <div className="empty">{t('No active rides in Piler right now')}</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t('STATUS')}</th>
                    <th>{t('ROUTE (PICKUP → DROP)')}</th>
                    <th>{t('FARE')}</th>
                    <th>{t('PASSENGER')}</th>
                    <th>{t('CAPTAIN')}</th>
                    <th>{t('ACTIONS')}</th>
                  </tr>
                </thead>
                <tbody>
                  {live.map((r) => {
                    const age = minutesAgo(toDate(r.createdAt));
                    const stuck = r.status === 'searching' && age >= 5;
                    const pTel = phoneHref(r.customerPhone);
                    const pWa = waHref(r.customerPhone);
                    const dTel = phoneHref(r.driverPhone);
                    return (
                      <tr key={r.id} className={stuck ? 'row-warn' : ''}>
                        <td>
                          <span className={`badge ${STATUS_CLASS[r.status] || 'gray'}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                          {stuck && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '2px', fontWeight: '600' }}>{age}m waiting</div>}
                        </td>
                        <td>
                          <div style={{ fontWeight: '600', color: '#ffffff' }}>{r.pickup?.name || r.pickup?.address || '—'}</div>
                          <div className="muted" style={{ fontSize: '12px' }}>➔ {r.destination?.name || r.destination?.address || '—'}</div>
                        </td>
                        <td>
                          <span style={{ fontWeight: '700', color: '#34d399', fontSize: '14px' }}>₹{r.fare}</span>
                        </td>
                        <td>
                          <div style={{ fontWeight: '600' }}>{r.customerName || 'Passenger'}</div>
                          <div className="muted" style={{ fontSize: '11px' }}>{r.customerPhone || '—'}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: '600', color: r.driverName ? '#ffffff' : '#94a3b8' }}>
                            {r.driverName || t('Finding…')}
                          </div>
                          {r.vehicleNumber && <div className="muted" style={{ fontSize: '11px' }}>{r.vehicleNumber}</div>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {pTel && (
                              <a href={pTel} className="btn btn-ghost btn-sm" title="Call Passenger">📞</a>
                            )}
                            {pWa && (
                              <a href={pWa} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="WhatsApp Passenger">💬</a>
                            )}
                            {dTel && (
                              <a href={dTel} className="btn btn-ghost btn-sm" title="Call Captain" style={{ color: '#34d399' }}>🛺</a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Live Online Fleet Snapshot */}
        <div className="card">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>🟢 {t('Active Captains')} ({stats.online})</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onNavigate?.('drivers')}
            >
              {t('Fleet List →')}
            </button>
          </div>

          {onlineDrivers.length === 0 ? (
            <div className="empty">{t('No captains currently online')}</div>
          ) : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {onlineDrivers.map((d) => {
                const tel = phoneHref(d.phone);
                const wa = waHref(d.phone, 'Namaste Anna, MyTownly Ops here.');
                return (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <div className="driver-avatar-wrap">
                      {d.photoUrl ? (
                        <img src={d.photoUrl} alt="" className="driver-avatar-img" />
                      ) : (
                        <div className="driver-avatar-fallback">🛺</div>
                      )}
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '13px', color: '#ffffff' }}>{d.name || 'Captain'}</div>
                        <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>{d.vehicleNumber || 'Auto'} · ⭐ {Number(d.ratingAvg || 5).toFixed(1)}</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      {tel && <a href={tel} className="btn btn-ghost btn-sm">📞</a>}
                      {wa && <a href={wa} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">💬</a>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
